import {
  AiUsageCallsDocument,
  AiUsageSummaryDocument,
  type AiUsageSummaryInput,
  LessonAiUsageDocument,
} from "@/generated/graphql";
import { useQuery } from "@apollo/client/react";

export function useAiUsageSummary(input: AiUsageSummaryInput) {
  const { data, loading, error } = useQuery(AiUsageSummaryDocument, {
    variables: { input },
    fetchPolicy: "cache-and-network",
  });

  return { summary: data?.aiUsageSummary ?? null, loading, error };
}

const POLL_INTERVAL_MS = 2000;

/** Every model call one draft run made. Skipped until a job is picked. */
export function useAiUsageCalls(jobId: string | null, isDrafting = false) {
  const { data, loading } = useQuery(AiUsageCallsDocument, {
    variables: { jobId: jobId ?? "" },
    skip: jobId === null,
    fetchPolicy: "cache-and-network",
    pollInterval: isDrafting ? POLL_INTERVAL_MS : 0,
  });

  return { calls: data?.aiUsageCalls ?? [], loading };
}

/** Polls while slides are being drafted, so a redraft's cost lands on its section as it finishes. */
export function useSectionCosts(jobId: string | null, isDrafting: boolean): Map<string, number> {
  const { calls } = useAiUsageCalls(jobId, isDrafting);

  const costs = new Map<string, number>();
  for (const call of calls) {
    costs.set(call.sectionId, (costs.get(call.sectionId) ?? 0) + call.costUsd);
  }
  return costs;
}

/**
 * What a filtered id is called — a lesson's path, an editor's name, a batch. Read from the ledger
 * over all time, so a lesson last drafted months ago still names itself inside a short range.
 */
export function useAiUsageLabel(
  groupBy: "lesson" | "editor" | "batch",
  id: string | undefined
): string {
  const filter =
    groupBy === "lesson"
      ? { lessonId: id }
      : groupBy === "editor"
        ? { userId: id }
        : { batchId: id };

  const { data } = useQuery(AiUsageSummaryDocument, {
    variables: { input: { groupBy, limit: 1, ...filter } },
    skip: id === undefined,
    fetchPolicy: "cache-first",
  });

  return data?.aiUsageSummary.rows[0]?.label ?? id ?? "";
}

export function useLessonAiUsage(lessonId: string) {
  const { data, loading } = useQuery(LessonAiUsageDocument, {
    variables: { lessonId },
    fetchPolicy: "cache-and-network",
  });

  return { usage: data?.lessonAiUsage ?? null, loading };
}
